import APIService from "../services/APIService";

/**
 * Platform Error Monitoring API Client
 *
 * Communicates with /api/v1/admin/errors/* endpoints for the
 * System Errors tab in the Admin Dashboard.
 */
const PlatformErrorAPI = {
  /**
   * Get error summary with category/severity aggregation and service health.
   * @param {number} hours - Time range in hours (default: 24)
   */
  getErrorSummary: async (hours = 24) => {
    try {
      const response = await APIService.get(
        `/api/v1/admin/errors/summary?hours=${hours}`,
      );
      return response;
    } catch (error) {
      console.error("[PlatformErrorAPI] Error fetching error summary:", error);
      throw error;
    }
  },

  /**
   * Get service health status.
   * @param {number} hours - Time range in hours (default: 24)
   */
  getServiceHealth: async (hours = 24) => {
    try {
      const response = await APIService.get(
        `/api/v1/admin/errors/health?hours=${hours}`,
      );
      return response;
    } catch (error) {
      console.error("[PlatformErrorAPI] Error fetching service health:", error);
      throw error;
    }
  },

  /**
   * Get paginated list of errors with optional filters.
   * @param {Object} params - Filter parameters
   * @param {string} [params.category] - Error category filter
   * @param {string} [params.severity] - Severity filter (CRITICAL, HIGH, MEDIUM, LOW)
   * @param {string} [params.sourceService] - Source service filter
   * @param {string} [params.status] - Status filter (NEW, ACKNOWLEDGED, RESOLVED)
   * @param {number} [params.hours] - Time range in hours
   * @param {number} [params.page] - Page number (0-based)
   * @param {number} [params.pageSize] - Items per page
   */
  getErrorList: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.category) queryParams.append("category", params.category);
      if (params.severity) queryParams.append("severity", params.severity);
      if (params.sourceService)
        queryParams.append("sourceService", params.sourceService);
      if (params.status) queryParams.append("status", params.status);
      if (params.hours) queryParams.append("hours", params.hours);
      if (params.page !== undefined) queryParams.append("page", params.page);
      if (params.pageSize) queryParams.append("pageSize", params.pageSize);

      const queryString = queryParams.toString();
      const endpoint = `/api/v1/admin/errors${queryString ? `?${queryString}` : ""}`;
      const response = await APIService.get(endpoint);
      return response;
    } catch (error) {
      console.error("[PlatformErrorAPI] Error fetching error list:", error);
      throw error;
    }
  },

  /**
   * Get full detail of a single error.
   * @param {string} category - Error category
   * @param {string} errorId - Error ID
   */
  getErrorDetail: async (category, errorId) => {
    try {
      const response = await APIService.get(
        `/api/v1/admin/errors/${encodeURIComponent(category)}/${encodeURIComponent(errorId)}`,
      );
      return response;
    } catch (error) {
      console.error("[PlatformErrorAPI] Error fetching error detail:", error);
      throw error;
    }
  },

  /**
   * Acknowledge an error (mark as under investigation).
   * @param {string} category - Error category
   * @param {string} errorId - Error ID
   * @param {string} [notes] - Optional notes
   */
  acknowledgeError: async (category, errorId, notes = null) => {
    try {
      const body = notes ? { notes } : {};
      const response = await APIService.put(
        `/api/v1/admin/errors/${encodeURIComponent(category)}/${encodeURIComponent(errorId)}/acknowledge`,
        body,
      );
      return response;
    } catch (error) {
      console.error("[PlatformErrorAPI] Error acknowledging error:", error);
      throw error;
    }
  },

  /**
   * Resolve an error (mark investigation complete).
   * @param {string} category - Error category
   * @param {string} errorId - Error ID
   * @param {string} [notes] - Optional resolution notes
   */
  resolveError: async (category, errorId, notes = null) => {
    try {
      const body = notes ? { notes } : {};
      const response = await APIService.put(
        `/api/v1/admin/errors/${encodeURIComponent(category)}/${encodeURIComponent(errorId)}/resolve`,
        body,
      );
      return response;
    } catch (error) {
      console.error("[PlatformErrorAPI] Error resolving error:", error);
      throw error;
    }
  },
};

export default PlatformErrorAPI;
